import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api';
import { useBack } from '../hooks';
import { useAuth } from '../context/AuthContext';
import { PackagingLayersEditor, packagingChain } from '../components/forms/PartForm';

// The Bulk Tool (the director, 30 Sep 2026). Pick a catalogue, tick the fields
// to change -- only the items where they are empty, or all of them, say for
// new cycle times after a new chiller -- then go through the items one at a
// time: the name on top, the ticked fields below, saved on every step.
//
//   Enter          save and go to the next item   (Ctrl+Enter in a text box)
//   Shift+Enter    save and go back
//   ← →            the same, when not typing in a field
//   Tab            take the previous item's value, shown grey in an empty field
//
// Every save goes through the server's checks and into the history.

const DARK = '#0f1117', CARD = '#1e2329', SUNK = '#131920', BORD = '#2a3040';
const MUTED = '#6b7280', DIM = '#9ca3af', TEXT = '#f0f2f5', TEAL = '#14b8a6', AMBER = '#f59e0b', RED = '#fca5a5';

const st = {
  card:  { border: `1px solid ${BORD}`, borderRadius: 14, background: CARD },
  input: { background: SUNK, border: `1px solid ${BORD}`, borderRadius: 8, color: TEXT, padding: '9px 11px', fontSize: 14, outline: 'none', fontFamily: 'system-ui', boxSizing: 'border-box' },
  btn:   { padding: '9px 18px', fontSize: 13, fontWeight: 700, border: `1px solid ${TEAL}`, borderRadius: 8, background: TEAL, color: '#fff', cursor: 'pointer', fontFamily: 'system-ui', whiteSpace: 'nowrap' },
  ghost: { padding: '8px 14px', fontSize: 13, fontWeight: 600, border: `1px solid ${BORD}`, borderRadius: 8, background: 'transparent', color: DIM, cursor: 'pointer', fontFamily: 'system-ui', whiteSpace: 'nowrap' },
  label: { fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em' },
};

const emptyVal = (field, v) => {
  if (field.kind === 'boolean') return false;
  if (field.kind === 'packaging') return !Array.isArray(v) || !v.length;
  return v === null || v === undefined || v === '';
};
const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
// A value as the server would store it, so '18' typed over 18 is not a change.
function norm(field, v) {
  if (v === '' || v === undefined) return null;
  if ((field.kind === 'number' || field.kind === 'integer') && v !== null) {
    const n = Number(String(v).replace(',', '.'));
    return Number.isFinite(n) ? n : v;
  }
  if ((field.kind === 'text' || field.kind === 'textarea') && typeof v === 'string') return v.trim() || null;
  return v;
}
const optionLabel = (field, v) => (field.options || []).find(o => String(o.value) === String(v))?.label ?? String(v);

export default function BulkTool() {
  const goBack = useBack('/admin');
  const { isDirector } = useAuth();

  const [catalogues, setCatalogues] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [step, setStep] = useState('pick');          // pick | fields | edit | done
  const [catKey, setCatKey] = useState(null);
  const [picked, setPicked] = useState([]);           // field keys, in catalogue order
  const [onlyEmpty, setOnlyEmpty] = useState(true);
  const [withRetired, setWithRetired] = useState(false);
  const [preview, setPreview] = useState(null);       // { count, total } | 'loading'
  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const [edits, setEdits] = useState({});             // item id -> values being typed
  const [state, setState] = useState(null);           // null | 'saving' | 'saved' | error sentence
  const [stale, setStale] = useState(false);
  const [tally, setTally] = useState({ saved: 0, visited: 0 });
  const [containers, setContainers] = useState([]);
  const firstField = useRef(null);

  useEffect(() => {
    api.get('/api/v1/bulk').then(r => setCatalogues(r.data.catalogues))
      .catch(e => setLoadError(e.response?.data?.error || 'The Bulk Tool could not be read just now.'));
    api.get('/api/v1/packaging-containers').then(r => setContainers(r.data || [])).catch(() => {});
  }, []);

  const cat = catalogues && catalogues.find(c => c.key === catKey);
  const fields = cat ? cat.fields.filter(f => picked.includes(f.key)) : [];
  const onlyBooleans = fields.length > 0 && fields.every(f => f.kind === 'boolean');
  const emptyParam = onlyEmpty && !onlyBooleans ? 1 : 0;
  const query = `fields=${fields.map(f => f.key).join(',')}&empty=${emptyParam}&retired=${withRetired ? 1 : 0}`;

  // How many items the choice covers, while choosing.
  useEffect(() => {
    if (step !== 'fields' || !fields.length) { setPreview(null); return; }
    let live = true;
    setPreview('loading');
    const t = setTimeout(() => {
      api.get(`/api/v1/bulk/${catKey}/items?${query}`)
        .then(r => { if (live) setPreview({ count: r.data.items.length, total: r.data.total }); })
        .catch(() => { if (live) setPreview(null); });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [step, catKey, query]); // eslint-disable-line react-hooks/exhaustive-deps

  function start() {
    setState('saving');
    api.get(`/api/v1/bulk/${catKey}/items?${query}`)
      .then(r => {
        setItems(r.data.items); setIndex(0); setEdits({}); setState(null); setStale(false);
        setTally({ saved: 0, visited: 0 });
        setStep(r.data.items.length ? 'edit' : 'done');
      })
      .catch(e => setState(e.response?.data?.error || 'The items could not be read just now.'));
  }

  const item = items[index];
  const current = item ? (edits[item.id] || item.values) : null;
  const differs = (f) => !same(norm(f, current[f.key]), norm(f, item.values[f.key]));
  const dirty = item ? fields.some(differs) : false;
  const previous = index > 0 ? (edits[items[index - 1].id] || items[index - 1].values) : null;
  const setValue = (key, v) => {
    setEdits(e => ({ ...e, [item.id]: { ...(e[item.id] || item.values), [key]: v } }));
    setState(null);
  };

  // Save if anything changed, then move; stay put, saying why, if the save is refused.
  const go = useCallback((delta) => {
    if (!item || state === 'saving') return;
    const move = () => {
      setTally(t => ({ ...t, visited: Math.max(t.visited, index + 1) }));
      const next = index + delta;
      if (next < 0) return;
      if (next >= items.length) { setStep('done'); return; }
      setIndex(next);
      setStale(false);
    };
    if (!dirty) { setState(null); move(); return; }
    const changed = Object.fromEntries(fields.filter(differs).map(f => [f.key, current[f.key]]));
    setState('saving');
    api.put(`/api/v1/bulk/${catKey}/${item.id}`, { values: changed, expect_updated_at: item.updated_at })
      .then(r => {
        setItems(list => list.map(x => (x.id === item.id
          ? { ...x, updated_at: r.data.updated_at || x.updated_at, values: { ...x.values, ...(r.data.values || changed) } }
          : x)));
        setEdits(e => { const n = { ...e }; delete n[item.id]; return n; });
        if (r.data.changed) setTally(t => ({ ...t, saved: t.saved + 1 }));
        setState(r.data.changed ? 'saved' : null);
        move();
      })
      .catch(e => {
        setState(e.response?.data?.error || 'That could not be saved just now; nothing was changed.');
        setStale(!!e.response?.data?.stale);
      });
  }, [item, state, dirty, current, index, items.length, catKey]); // eslint-disable-line react-hooks/exhaustive-deps

  function reloadItem() {
    api.get(`/api/v1/bulk/${catKey}/items?${query}&ids=${item.id}`).then(r => {
      const fresh = r.data.items[0];
      if (fresh) setItems(list => list.map(x => (x.id === fresh.id ? fresh : x)));
      setEdits(e => { const n = { ...e }; delete n[item.id]; return n; });
      setState(null); setStale(false);
    });
  }

  // ← → when not typing; Enter / Shift+Enter in a field.
  useEffect(() => {
    if (step !== 'edit') return;
    function onKey(e) {
      const tag = (e.target.tagName || '').toLowerCase();
      const inField = tag === 'input' || tag === 'textarea' || tag === 'select';
      if (e.key === 'Enter' && inField) {
        if (tag === 'textarea' && !e.ctrlKey) return;
        if (e.defaultPrevented) return;   // already handled, e.g. Enter adding a new container
        e.preventDefault();
        go(e.shiftKey ? -1 : 1);
        return;
      }
      if (inField) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, go]);

  // Ready to type as soon as an item appears.
  useEffect(() => {
    if (step === 'edit' && firstField.current) firstField.current.focus();
  }, [step, index]);

  function addContainer(name) {
    return api.post('/api/v1/packaging-containers', { name }).then(r => {
      setContainers(cs => (cs.some(c => c.id === r.data.id) ? cs
        : [...cs, r.data].sort((a, b) => (a.sort_order - b.sort_order) || a.name.localeCompare(b.name))));
      return r.data;
    });
  }

  if (!isDirector()) return <Page goBack={goBack}><div style={{ ...st.card, padding: 18, color: DIM }}>The Bulk Tool changes many records at once, so it is for directors.</div></Page>;
  if (loadError) return <Page goBack={goBack}><div style={{ ...st.card, padding: 18, color: RED }}>{loadError}</div></Page>;
  if (!catalogues) return <Page goBack={goBack}><div style={{ color: MUTED, fontSize: 13 }}>Reading the catalogues…</div></Page>;

  // ── 1. which catalogue ──
  if (step === 'pick') {
    return (
      <Page goBack={goBack} sub="Change many items one after another: pick what to edit, tick the fields, then step through.">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
          {catalogues.map(c => (
            <button key={c.key} type="button" onClick={() => { setCatKey(c.key); setPicked([]); setStep('fields'); }}
              style={{ ...st.card, padding: '16px 18px', textAlign: 'left', cursor: 'pointer', color: TEXT, fontFamily: 'system-ui' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontSize: 15, fontWeight: 700 }}>{c.label}</span>
                <span style={{ fontSize: 12, color: MUTED }}>{c.count ?? '—'}</span>
              </div>
              <div style={{ fontSize: 12, color: DIM, marginTop: 6, lineHeight: 1.45 }}>{c.about}</div>
            </button>
          ))}
        </div>
      </Page>
    );
  }

  // ── 2. which fields ──
  if (step === 'fields') {
    const toggle = k => setPicked(p => (p.includes(k) ? p.filter(x => x !== k) : [...p, k]));
    return (
      <Page goBack={() => setStep('pick')} backLabel="Catalogues" sub={`${cat.label}: tick what you want to change.`}>
        <div style={{ ...st.card, padding: '6px 0' }}>
          <div style={{ display: 'flex', gap: 8, padding: '10px 18px', borderBottom: `1px solid ${BORD}` }}>
            <button type="button" style={st.ghost} onClick={() => setPicked(cat.fields.map(f => f.key))}>All</button>
            <button type="button" style={st.ghost} onClick={() => setPicked([])}>None</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', padding: '8px 10px' }}>
            {cat.fields.map(f => (
              <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 8px', cursor: 'pointer', borderRadius: 8, background: picked.includes(f.key) ? 'rgba(20,184,166,0.08)' : 'transparent' }}>
                <input type="checkbox" checked={picked.includes(f.key)} onChange={() => toggle(f.key)} style={{ accentColor: TEAL, width: 15, height: 15 }} />
                <span style={{ fontSize: 13.5, color: TEXT }}>{f.label}{f.unit ? <span style={{ color: MUTED }}> ({f.unit})</span> : null}</span>
              </label>
            ))}
          </div>
        </div>

        <div style={{ ...st.card, padding: '14px 18px', marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: onlyBooleans ? 'default' : 'pointer', opacity: onlyBooleans ? 0.5 : 1 }}>
            <input type="checkbox" checked={onlyEmpty && !onlyBooleans} disabled={onlyBooleans} onChange={e => setOnlyEmpty(e.target.checked)} style={{ accentColor: TEAL, width: 15, height: 15 }} />
            <span style={{ fontSize: 13.5 }}>Only items where these fields are empty or incomplete</span>
          </label>
          <div style={{ fontSize: 12, color: MUTED, marginLeft: 25, marginTop: -6 }}>
            {onlyBooleans ? 'Yes/no fields are never empty, so every item is shown.' : 'Untick to go through the filled ones too, for example new cycle times after a new chiller.'}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <input type="checkbox" checked={withRetired} onChange={e => setWithRetired(e.target.checked)} style={{ accentColor: TEAL, width: 15, height: 15 }} />
            <span style={{ fontSize: 13.5 }}>Include discontinued and decommissioned ones</span>
          </label>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 14, marginTop: 14 }}>
          <span style={{ fontSize: 13, color: DIM }}>
            {!fields.length ? 'Tick at least one field.'
              : preview === 'loading' ? 'Counting…'
              : preview ? `${preview.count} ${preview.count === 1 ? 'item' : 'items'} to go through${emptyParam ? ` (of ${preview.total})` : ''}` : ''}
          </span>
          {typeof state === 'string' && state !== 'saving' && <span style={{ fontSize: 12, color: RED }}>{state}</span>}
          <button type="button" style={{ ...st.btn, opacity: fields.length && preview && preview !== 'loading' && preview.count ? 1 : 0.5 }}
            disabled={!fields.length || !preview || preview === 'loading' || !preview.count || state === 'saving'} onClick={start}>
            Start →
          </button>
        </div>
      </Page>
    );
  }

  // ── 4. done ──
  if (step === 'done') {
    return (
      <Page goBack={goBack} sub={`${cat.label} · ${fields.map(f => f.label).join(', ')}`}>
        <div style={{ ...st.card, padding: '28px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{items.length ? 'All done' : 'Nothing to do'}</div>
          <div style={{ fontSize: 14, color: DIM, marginTop: 8 }}>
            {items.length
              ? `${tally.saved} of ${items.length} ${items.length === 1 ? 'item' : 'items'} changed and saved; every change is in the history.`
              : 'Every item already has these fields filled in.'}
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 20 }}>
            {items.length > 0 && <button type="button" style={st.ghost} onClick={() => { setIndex(items.length - 1); setStep('edit'); }}>← Back to the last one</button>}
            <button type="button" style={st.ghost} onClick={() => setStep('fields')}>Change the fields</button>
            <button type="button" style={st.btn} onClick={() => setStep('pick')}>Another catalogue</button>
          </div>
        </div>
      </Page>
    );
  }

  // ── 3. one item at a time ──
  const progress = items.length ? ((index + 1) / items.length) * 100 : 0;
  const stored = item.in_product && catKey === 'parts';
  return (
    <Page goBack={() => { if (!dirty || window.confirm('This item has changes that are not saved yet. Leave without saving them?')) setStep('fields'); }}
      backLabel="Fields" sub={`${cat.label} · ${fields.map(f => f.label).join(', ')}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <div style={{ flex: 1, height: 6, background: SUNK, borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ width: `${progress}%`, height: '100%', background: TEAL, transition: 'width 0.2s' }} />
        </div>
        <span style={{ fontSize: 13, color: DIM, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{index + 1} of {items.length}</span>
        <select value={index} onChange={e => { if (!dirty) { setIndex(Number(e.target.value)); setState(null); } else setState('Save or undo the changes on this item first (Enter saves and moves on).'); }}
          style={{ ...st.input, padding: '6px 8px', fontSize: 12, maxWidth: 220 }} title="Jump to an item">
          {items.map((x, i) => <option key={x.id} value={i}>{i + 1}. {x.title}</option>)}
        </select>
      </div>

      <div style={st.card}>
        {/* Which item this is, always on top */}
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${BORD}` }}>
          <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.25 }}>{item.title}</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4, flexWrap: 'wrap' }}>
            {item.subtitle && <span style={{ fontSize: 13, color: TEAL, fontFamily: 'monospace' }}>{item.subtitle}</span>}
            {stored && <span style={{ fontSize: 11, color: DIM, border: `1px solid ${BORD}`, borderRadius: 5, padding: '1px 7px' }}>goes into a product — stored until assembly</span>}
            {item.retired && <span style={{ fontSize: 11, color: AMBER, border: '1px solid rgba(245,158,11,0.4)', borderRadius: 5, padding: '1px 7px' }}>discontinued</span>}
          </div>
        </div>

        <div style={{ padding: '6px 20px 14px' }}>
          {fields.map((f, i) => (
            <FieldRow key={f.key} field={f} value={current[f.key]} ghost={previous ? previous[f.key] : null}
              onChange={v => setValue(f.key, v)} inputRef={i === 0 ? firstField : null}
              changed={differs(f)}
              stored={stored} containers={containers} onAddContainer={addContainer} />
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
        <button type="button" style={{ ...st.ghost, opacity: index === 0 ? 0.4 : 1 }} disabled={index === 0 || state === 'saving'} onClick={() => go(-1)}>← Previous</button>
        <div style={{ flex: 1, textAlign: 'center', fontSize: 12.5, color: state === 'saved' ? TEAL : state && state !== 'saving' ? RED : MUTED }}>
          {state === 'saving' ? 'Saving…'
            : state === 'saved' ? '✓ Saved'
            : state ? <>{state} {stale && <button type="button" style={{ ...st.ghost, padding: '3px 9px', fontSize: 12, marginLeft: 6 }} onClick={reloadItem}>Reload this item</button>}</>
            : dirty ? 'Not saved yet — moving on saves it' : ''}
        </div>
        <button type="button" style={st.btn} disabled={state === 'saving'} onClick={() => go(1)}>
          {dirty ? 'Save & next →' : index === items.length - 1 ? 'Finish →' : 'Next →'}
        </button>
      </div>
      <div style={{ fontSize: 11.5, color: MUTED, textAlign: 'center', marginTop: 10, lineHeight: 1.6 }}>
        Enter saves and moves on · Shift+Enter goes back · ← → when not typing · Tab takes the grey value from the previous item
      </div>
    </Page>
  );
}

// One field of the item being edited, with the previous item's value shown
// grey when this one is empty -- Tab (or a click) takes it.
function FieldRow({ field, value, ghost, onChange, inputRef, changed, stored, containers, onAddContainer }) {
  const hasGhost = emptyVal(field, value) && !emptyVal(field, ghost) && field.kind !== 'boolean';
  const takeGhost = e => {
    if (e.key === 'Tab' && !e.shiftKey && hasGhost) onChange(ghost);
  };
  const ghostText = !hasGhost ? null
    : field.kind === 'select' || field.kind === 'ref' ? optionLabel(field, ghost)
    : field.kind === 'packaging' ? packagingChain(ghost, containers)
    : String(ghost);
  const notForStored = stored && (field.key === 'packing_count_method' || field.key === 'packing_place');

  let control;
  if (notForStored) {
    control = <div style={{ fontSize: 12.5, color: MUTED, padding: '9px 0' }}>Not used for a part that goes into a product: it is stored, not packed.</div>;
  } else if (field.kind === 'boolean') {
    control = (
      <div style={{ display: 'flex', gap: 6 }}>
        {[[true, 'Yes'], [false, 'No']].map(([v, l], i) => (
          <button key={l} type="button" ref={i === 0 ? inputRef : null} onClick={() => onChange(v)}
            style={{ ...st.ghost, padding: '8px 16px', color: value === v ? TEAL : DIM, borderColor: value === v ? TEAL : BORD, background: value === v ? 'rgba(20,184,166,0.1)' : 'transparent' }}>{l}</button>
        ))}
      </div>
    );
  } else if (field.kind === 'select' || field.kind === 'ref') {
    control = (
      <select ref={inputRef} value={value ?? ''} onKeyDown={takeGhost} onChange={e => onChange(e.target.value === '' ? null : (field.kind === 'select' && typeof field.options[0]?.value === 'number' ? Number(e.target.value) : (field.kind === 'ref' && field.ref !== 'colours' ? Number(e.target.value) : e.target.value)))}
        style={{ ...st.input, width: '100%', maxWidth: 420, color: value == null ? MUTED : TEXT }}>
        <option value="">— not set —</option>
        {(field.options || []).map(o => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
      </select>
    );
  } else if (field.kind === 'textarea') {
    control = (
      <textarea ref={inputRef} value={value ?? ''} placeholder={hasGhost ? ghostText : ''} onKeyDown={takeGhost} onChange={e => onChange(e.target.value)}
        style={{ ...st.input, width: '100%', minHeight: 70, resize: 'vertical' }} />
    );
  } else if (field.kind === 'packaging') {
    control = (
      <div>
        <PackagingLayersEditor layers={Array.isArray(value) ? value : []} containers={containers} onAddContainer={onAddContainer}
          onChange={layers => onChange(layers)} unit={stored ? 'parts' : field.unit}
          label={stored ? 'Stored in — from the part outwards' : `Packed as — from the ${field.unit === 'products' ? 'product' : 'part'} outwards`} />
      </div>
    );
  } else {
    control = (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input ref={inputRef} value={value ?? ''} inputMode={field.kind === 'text' ? 'text' : 'decimal'}
          placeholder={hasGhost ? ghostText : ''} onKeyDown={takeGhost} onChange={e => onChange(e.target.value)}
          style={{ ...st.input, width: field.kind === 'text' ? '100%' : 160, maxWidth: 420 }} />
        {field.unit && <span style={{ fontSize: 13, color: MUTED }}>{field.unit}</span>}
      </div>
    );
  }

  return (
    <div style={{ padding: '12px 0', borderBottom: `1px solid ${BORD}` }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 7 }}>
        <span style={st.label}>{field.label}</span>
        {changed && <span style={{ fontSize: 11, color: AMBER }}>changed</span>}
      </div>
      {control}
      {hasGhost && !notForStored && (
        <button type="button" onClick={() => onChange(ghost)}
          style={{ background: 'none', border: 'none', padding: '5px 0 0', cursor: 'pointer', fontSize: 12, color: MUTED, fontFamily: 'system-ui', textAlign: 'left' }}>
          Previous item: <span style={{ color: DIM }}>{ghostText}</span> — Tab or click to use it
        </button>
      )}
    </div>
  );
}

function Page({ goBack, backLabel = 'Admin', sub, children }) {
  return (
    <div style={{ padding: 24, fontFamily: 'system-ui, sans-serif', color: TEXT, background: DARK, minHeight: '100vh', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: 820, margin: '0 auto' }}>
        <button type="button" onClick={goBack} style={{ background: 'none', border: 'none', color: MUTED, cursor: 'pointer', fontSize: 13, padding: 0, marginBottom: 14, fontFamily: 'system-ui' }}>
          <i className="fa-solid fa-chevron-left" style={{ fontSize: 10, marginRight: 6 }} />{backLabel}
        </button>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 4px' }}>Bulk Tool</h1>
        {sub && <div style={{ fontSize: 13, color: DIM, marginBottom: 18 }}>{sub}</div>}
        {children}
      </div>
    </div>
  );
}
